import DeleteIcon from "@mui/icons-material/Delete";
import { Box } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "@/ui/SystemButton/SystemButton";
import type { V1OnlyLocationEditorDesign } from "./consts";

type Props = {
	/** v1 only until its first v2 consumer (the Locations page's expanded editor, APP-780 PR4) brings the v2 markup. */
	design: V1OnlyLocationEditorDesign;
	/** The second step: the admin pressed Delete and is asked to confirm. */
	confirming: boolean;
	onConfirmingChange: (confirming: boolean) => void;
	onDelete: () => Promise<void>;
	deleting: boolean;
	/** False while shifts are assigned to the location. */
	canDelete: boolean;
	/** The admin's account can't edit the schedule template. */
	editBlocked: boolean;
};

/** Deletes the whole location row, in two steps: Delete, then Confirm or Cancel. */
export const DeleteRowControl: FC<Props> = ({ design, confirming, onConfirmingChange, onDelete, deleting, canDelete, editBlocked }): ReactElement => {
	const { t } = useTranslation();
	switch (design) {
		case "v1":
			return !confirming ? (
				<SystemButton
					sx={{
						fontWeight: 500,
					}}
					leadingIcon={<DeleteIcon fontSize={"small"} />}
					buttonVariant={"delete"}
					disabled={!canDelete || editBlocked}
					tooltip={editBlocked ? t("schedule.template.edit.blocked") : !canDelete ? t("schedule.template.v3.editor.cannotDelete") : undefined}
					onClick={(e) => {
						e.stopPropagation();
						onConfirmingChange(true);
					}}
					data-testid="location-delete-row-button"
				>
					{t("schedule.template.v3.editor.deleteRow")}
				</SystemButton>
			) : (
				<Box
					sx={{
						display: "flex",
						gap: 1,
					}}
				>
					<SystemButton
						sx={{
							fontWeight: 500,
						}}
						leadingIcon={<DeleteIcon fontSize={"small"} />}
						buttonVariant={"delete"}
						onClick={async (e) => {
							e.stopPropagation();
							await onDelete();
						}}
						loading={deleting}
						data-testid="location-delete-row-confirm-button"
					>
						{t("schedule.template.v3.editor.confirmDelete")}
					</SystemButton>
					<SystemButton
						buttonVariant={"secondary"}
						onClick={(e) => {
							e.stopPropagation();
							onConfirmingChange(false);
						}}
						disabled={deleting}
						data-testid="location-delete-row-cancel-button"
					>
						{t("cancel")}
					</SystemButton>
				</Box>
			);
	}
};
